<template>
  <Modal ref="modalRef" @after-close="clearForm">
    <template #header>Edit contact details</template>

    <template #body>
      <FormGroup gap="medium" class="form">
        <div class="name-fields">
          <FormField label="First name" :error="form.fieldErrors('firstName')">
            <Input v-model="form.fields.firstName" autocomplete="off" />
          </FormField>
          <FormField label="Last name" :error="form.fieldErrors('lastName')">
            <Input v-model="form.fields.lastName" autocomplete="off" />
          </FormField>
        </div>

        <FormField label="Phone number" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>

        <Hint icon="info-circle">
          Renters get this number for their tour, and Tallo texts it if a renter cancels or can't get in. Changes apply
          everywhere this agent is listed, including tours already booked with them.
        </Hint>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close">Cancel</Button>
      <Button @click="save" :loading="form.submitting">Save</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { reactive, ref } from 'vue';

import {
  Button,
  FormField,
  FormGroup,
  Hint,
  Input,
  InputPhone,
  Modal,
  phoneNumberValidator,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';

import { ShowingAgentDto } from '../interfaces/showing-agent.dto';
import { showingAgentsQueryKey } from '../queries/showing-agents.query';
import showingAgentsService from '../services/showing-agents.service';

const emit = defineEmits<{ saved: [showingAgent: ShowingAgentDto] }>();

const queryCache = useQueryCache();
const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const showingAgent = ref<ShowingAgentDto>();

const blank = { firstName: '', lastName: '', phone: '' };
const fields = reactive({ ...blank });
const form = useForm(fields, {
  firstName: { required: helpers.withMessage('First name is required', required) },
  lastName: { required: helpers.withMessage('Last name is required', required) },
  phone: { phoneNumberValidator, required: helpers.withMessage('Phone number is required', required) },
});

function open(agent: ShowingAgentDto) {
  showingAgent.value = agent;
  Object.assign(fields, { firstName: agent.firstName, lastName: agent.lastName, phone: agent.phone ?? '' });
  form.resetValidation();
  modalRef.value?.show();
}

async function save() {
  const agent = showingAgent.value;

  if (!agent) {
    return;
  }

  try {
    await form.submit(async (contact) => {
      const changes = { firstName: contact.firstName.trim(), lastName: contact.lastName.trim(), phone: contact.phone };

      await showingAgentsService.update(agent.id, changes);
      // The roster and every unit's list show this agent, so they all refresh
      await queryCache.invalidateQueries({ key: [showingAgentsQueryKey] });

      modalRef.value?.close();
      emit('saved', { ...agent, ...changes });
    });
  } catch (error) {
    // A rejected field lands on the field itself; anything else from the server is a toast
    if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      alertsStore.catchError(error);
    }
  }
}

function clearForm() {
  Object.assign(fields, blank);
  form.resetValidation();
}

defineExpose({ open });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.form {
  width: 27rem;
  max-width: 100%;
}

.name-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--t-spacing-md);

  @include mobile {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
